import {
  asJsonFieldValueOrNull,
  type JsonFieldValue,
  type JsonObject,
} from "@/types/json"

export interface InitializationParameterDraftRow {
  id: number
  key: string
  value: string
  originalValue?: JsonFieldValue
  valueChanged: boolean
}

let nextRowId = 0

export function formatInitializationParameterValue(
  value: JsonFieldValue,
): string {
  return typeof value === "string" ? value : (JSON.stringify(value) ?? "")
}

export function createInitializationParameterDraftRow(
  key = "",
  value = "",
): InitializationParameterDraftRow {
  return {
    id: nextRowId++,
    key,
    value,
    valueChanged: true,
  }
}

export function initializationParametersToRows(
  record: JsonObject,
): InitializationParameterDraftRow[] {
  const rows = Object.entries(record).map(([key, value]) => ({
    id: nextRowId++,
    key,
    value: formatInitializationParameterValue(value),
    originalValue: value,
    valueChanged: false,
  }))

  return rows.length > 0 ? rows : [createInitializationParameterDraftRow()]
}

function parseEditedInitializationParameterValue(
  value: string,
  originalValue?: JsonFieldValue,
): JsonFieldValue {
  const trimmedValue = value.trim()
  if (originalValue === undefined || typeof originalValue === "string") {
    return trimmedValue
  }

  try {
    const parsedValue: unknown = JSON.parse(trimmedValue)
    return parsedValue === null
      ? null
      : (asJsonFieldValueOrNull(parsedValue) ?? trimmedValue)
  } catch {
    return trimmedValue
  }
}

export function initializationParameterRowsToRecord(
  rows: InitializationParameterDraftRow[],
): JsonObject {
  const record: JsonObject = {}

  for (const row of rows) {
    const key = row.key.trim()
    if (!key) {
      continue
    }

    record[key] =
      row.valueChanged || row.originalValue === undefined
        ? parseEditedInitializationParameterValue(row.value, row.originalValue)
        : row.originalValue
  }

  return record
}
